import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash,
  PencilSimple,
  X,
  ArrowUp,
  ArrowDown,
  Download,
  CaretDown,
  CaretUp,
  Check,
  ArrowsClockwise,
  Storefront,
} from "@phosphor-icons/react";
import { ledgerAPI, ebayAPI } from "@/lib/api";
import BottomNav from "@/components/app/BottomNav";
import { useConfirm } from "@/components/app/ConfirmDialog";

const TODAY = () => new Date().toISOString().slice(0, 10);

function fmtMoney(n) {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}$${Math.abs(v).toFixed(2)}`;
}

function isPendingPayout(t) {
  const status = (t?.external_meta?.status || "").toUpperCase();
  return t?.external_source === "ebay_payout" && (status === "INITIATED" || status === "IN_PROCESS");
}

function PendingBadge() {
  return (
    <span
      className="inline-block ml-1.5 align-[-1px] border border-amber-600 bg-amber-50 text-amber-800 text-[10px] font-bold uppercase px-1.5 py-0.5"
      data-testid="payout-pending-badge"
      title="eBay has initiated this payout but the bank has not yet confirmed it."
    >
      Pending
    </span>
  );
}

function SectionHeader({ title, right }) {
  return (
    <div className="flex items-end justify-between border-b-2 border-black pb-2 mb-4">
      <div className="font-heading text-lg font-bold tracking-tight uppercase">
        {title}
      </div>
      {right}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-body text-[11px] font-semibold tracking-[0.1em] uppercase text-neutral-600 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

// ----- Add / Edit Transaction Form ----------------------------------------
function TransactionForm({ initial, categories, onSave, onCancel, submitting, onRequestNewCategory }) {
  const [date, setDate] = useState(initial?.date || TODAY());
  const [description, setDescription] = useState(initial?.description || "");
  const [amount, setAmount] = useState(initial?.amount ? String(initial.amount) : "");
  const [type, setType] = useState(initial?.type || "debit");
  const [category, setCategory] = useState(initial?.category || "");
  const [error, setError] = useState("");

  const submit = () => {
    setError("");
    const amt = parseFloat(amount);
    if (!date) return setError("Date is required");
    if (!amt || amt <= 0) return setError("Amount must be greater than 0");
    if (!type) return setError("Choose Deposit or Debit");
    onSave({
      date,
      description: description.trim(),
      amount: amt,
      type,
      category: category.trim(),
    });
  };

  return (
    <div className="space-y-3" data-testid="txn-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Date">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="block w-full min-w-0 box-border appearance-none border-2 border-black bg-white px-3 py-2 font-body text-sm focus:outline-none focus:ring-4 focus:ring-black/20"
            data-testid="txn-date"
          />
        </Field>
        <Field label="Amount">
          <div className="flex items-center gap-1">
            <span className="font-heading text-lg font-bold">$</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setAmount(v);
              }}
              className="flex-1 border-2 border-black bg-white px-3 py-2 font-body text-sm focus:outline-none focus:ring-4 focus:ring-black/20"
              data-testid="txn-amount"
            />
          </div>
        </Field>
      </div>

      <Field label="Category">
        <select
          value={category}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "__add_new__") {
              // Don't update category — open the manager modal instead
              if (onRequestNewCategory) onRequestNewCategory();
              return;
            }
            setCategory(val);
          }}
          className="w-full border-2 border-black bg-white px-3 py-2 font-body text-sm focus:outline-none focus:ring-4 focus:ring-black/20"
          data-testid="txn-category"
        >
          <option value="">— None —</option>
          {categories.map((c) => (
            <option key={c.id} value={c.name}>
              {c.name}
            </option>
          ))}
          <option value="__add_new__">+ Add new category…</option>
        </select>
      </Field>

      <Field label="Description">
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What was this for?"
          className="w-full border-2 border-black bg-white px-3 py-2 font-body text-sm focus:outline-none focus:ring-4 focus:ring-black/20"
          data-testid="txn-description"
        />
      </Field>

      <Field label="Type">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setType("deposit")}
            className={`border-2 border-black py-2 font-heading text-sm font-bold uppercase transition-all ${
              type === "deposit"
                ? "bg-green-600 text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
                : "bg-white text-black hover:bg-neutral-100"
            }`}
            data-testid="txn-type-deposit"
          >
            <ArrowUp size={14} weight="bold" className="inline mr-1" /> Deposit
          </button>
          <button
            type="button"
            onClick={() => setType("debit")}
            className={`border-2 border-black py-2 font-heading text-sm font-bold uppercase transition-all ${
              type === "debit"
                ? "bg-red-600 text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
                : "bg-white text-black hover:bg-neutral-100"
            }`}
            data-testid="txn-type-debit"
          >
            <ArrowDown size={14} weight="bold" className="inline mr-1" /> Debit
          </button>
        </div>
      </Field>

      {error && (
        <div className="border-2 border-red-600 bg-red-50 text-red-700 font-body text-sm px-3 py-2" data-testid="txn-form-error">
          {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="flex-1 bg-black text-white border-2 border-black px-4 py-2.5 font-heading text-sm font-bold uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] disabled:opacity-50 hover:bg-neutral-800"
          data-testid="txn-form-save"
        >
          {submitting ? "Saving..." : initial ? "Save Changes" : "Add Transaction"}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="border-2 border-black px-4 py-2.5 font-heading text-sm font-bold uppercase bg-white hover:bg-neutral-100"
            data-testid="txn-form-cancel"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

// ----- Category Manager (inline modal) -----------------------------------
function CategoryManager({ categories, onAdd, onDelete, onClose }) {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const name = input.trim();
    if (!name) return;
    setBusy(true);
    try {
      await onAdd(name);
      setInput("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[10001] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="category-manager-modal"
    >
      <div
        className="w-full sm:max-w-md bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b-2 border-black p-4">
          <div className="font-heading text-lg font-bold uppercase tracking-tight">
            Categories
          </div>
          <button onClick={onClose} className="hover:opacity-60" data-testid="category-manager-close">
            <X size={22} weight="bold" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="Add new category..."
              maxLength={60}
              className="flex-1 border-2 border-black bg-white px-3 py-2 font-body text-sm focus:outline-none focus:ring-4 focus:ring-black/20"
              data-testid="new-category-input"
            />
            <button
              onClick={submit}
              disabled={busy || !input.trim()}
              className="bg-black text-white border-2 border-black px-3 py-2 font-heading text-sm font-bold uppercase disabled:opacity-50 hover:bg-neutral-800"
              data-testid="new-category-add"
            >
              <Plus size={16} weight="bold" />
            </button>
          </div>

          {categories.length === 0 ? (
            <div className="font-body text-sm text-neutral-500 italic">
              No categories yet. Add your first one above.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {categories.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between border-2 border-black bg-white px-3 py-2"
                  data-testid={`category-row-${c.name}`}
                >
                  <span className="font-body text-sm">{c.name}</span>
                  <button
                    onClick={() => onDelete(c)}
                    className="text-red-600 hover:text-red-800"
                    data-testid={`category-delete-${c.name}`}
                    aria-label={`Delete ${c.name}`}
                  >
                    <Trash size={18} weight="bold" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ----- Table column sort header -----------------------------------------
function SortHeader({ field, label, sort, order, onSort, align = "left" }) {
  const active = sort === field;
  const alignClass = align === "right" ? "text-right justify-end" : "text-left";
  return (
    <th
      className={`px-3 py-2 font-heading text-[11px] font-bold uppercase tracking-wide cursor-pointer select-none border-b-2 border-black bg-neutral-100 hover:bg-neutral-200 ${alignClass}`}
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
    >
      <span className={`inline-flex items-center gap-1 ${alignClass}`}>
        {label}
        {active && (order === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)}
      </span>
    </th>
  );
}

// ----- Main page --------------------------------------------------------
export default function LedgerPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [balance, setBalance] = useState({ balance: 0, total_deposits: 0, total_debits: 0 });
  const [summary, setSummary] = useState({ monthly: [], by_category: [], total_deposits: 0, total_debits: 0, net: 0 });

  const [filters, setFilters] = useState({ from_date: "", to_date: "", category: "", type: "", q: "" });
  const [sort, setSort] = useState("date");
  const [order, setOrder] = useState("desc");
  const [pageSize, setPageSize] = useState(5);
  const [page, setPage] = useState(1);

  const [editing, setEditing] = useState(null); // txn object or null
  const [showAdd, setShowAdd] = useState(false);
  const [showCatMgr, setShowCatMgr] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savedFlash, setSavedFlash] = useState("");

  // eBay payouts → ledger sync
  const [ebaySync, setEbaySync] = useState({
    status: "loading", // 'loading' | 'not_connected' | 'scope_missing' | 'ready'
    last_sync_at: null,
    payouts: [],
    already_imported_ids: [],
  });
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const loadEbaySync = useCallback(async () => {
    try {
      const r = await ebayAPI.payoutsPreview();
      setEbaySync(r.data);
      return r.data;
    } catch (e) {
      const fallback = { status: "not_connected", payouts: [], last_sync_at: null, already_imported_ids: [] };
      setEbaySync(fallback);
      return fallback;
    }
  }, []);

  const handleSyncPayouts = async (forceFull = false) => {
    setSyncing(true);
    setSyncMsg("");
    try {
      const r = forceFull
        ? await ebayAPI.payoutsSyncFull()
        : await ebayAPI.payoutsSync();
      const { imported, updated, skipped, pending, total_amount, discovered_via_transactions } = r.data;
      const parts = [];
      if (imported > 0) {
        parts.push(`Imported ${imported} payout${imported !== 1 ? "s" : ""} ($${total_amount.toFixed(2)})`);
      }
      if (updated > 0) {
        parts.push(`updated ${updated}`);
      }
      if (pending > 0) {
        parts.push(`${pending} pending`);
      }
      if (discovered_via_transactions > 0) {
        parts.push(`${discovered_via_transactions} recovered via transactions`);
      }
      if (parts.length > 0) {
        setSyncMsg(parts.join(" · "));
      } else {
        setSyncMsg(skipped > 0 ? `Up to date (${skipped} already imported)` : "No new payouts found");
      }
      await Promise.all([loadAll(), loadEbaySync()]);
      setTimeout(() => setSyncMsg(""), 4000);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Sync failed. Try again.";
      setSyncMsg(detail);
      // If scope missing, refresh status so UI shows the reconnect CTA
      if (e?.response?.status === 403) await loadEbaySync();
      setTimeout(() => setSyncMsg(""), 6000);
    } finally {
      setSyncing(false);
    }
  };

  const handleImportById = async () => {
    const raw = window.prompt(
      "Paste the eBay Payout ID to import. You can find it in eBay Seller Hub → Payments → Payouts → the specific payout's details."
    );
    if (!raw) return;
    const pid = raw.trim();
    if (!/^\d+$/.test(pid)) {
      setSyncMsg("Payout ID must be numbers only.");
      setTimeout(() => setSyncMsg(""), 4000);
      return;
    }
    setSyncing(true);
    setSyncMsg("");
    try {
      const r = await ebayAPI.payoutsImportById(pid);
      const action = r.data.action;
      const amt = r.data.payout?.amount ?? 0;
      if (action === "imported") {
        setSyncMsg(`Imported payout ${pid} ($${Number(amt).toFixed(2)})`);
      } else if (action === "updated") {
        setSyncMsg(`Updated payout ${pid} ($${Number(amt).toFixed(2)})`);
      } else {
        setSyncMsg(`Payout ${pid} already imported, no changes.`);
      }
      await Promise.all([loadAll(), loadEbaySync()]);
      setTimeout(() => setSyncMsg(""), 5000);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Import failed. Try again.";
      setSyncMsg(detail);
      setTimeout(() => setSyncMsg(""), 6000);
    } finally {
      setSyncing(false);
    }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, order };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const [txnsRes, catsRes, balRes, summRes] = await Promise.all([
        ledgerAPI.listTransactions(params),
        ledgerAPI.listCategories(),
        ledgerAPI.balance(),
        ledgerAPI.summary({ from_date: filters.from_date || undefined, to_date: filters.to_date || undefined }),
      ]);
      setTransactions(txnsRes.data.transactions || []);
      setCategories(catsRes.data.categories || []);
      setBalance(balRes.data);
      setSummary(summRes.data);
    } catch (e) {
      console.error("Ledger load failed", e);
    } finally {
      setLoading(false);
    }
  }, [filters, sort, order]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Sentinel so we only auto-sync once per page-session, even on re-renders.
  const autoSyncedRef = useRef(false);

  useEffect(() => {
    (async () => {
      const data = await loadEbaySync();
      // Opportunistic auto-sync: if eBay is connected and the last sync was
      // either never or >24h ago, run a quiet sync in the background. The user
      // sees the result toast on the sync card when it finishes.
      if (autoSyncedRef.current) return;
      if (!data || data.status !== "ready") return;
      const lastSyncMs = data.last_sync_at ? Date.parse(data.last_sync_at) : 0;
      const stale = !lastSyncMs || Date.now() - lastSyncMs > 24 * 60 * 60 * 1000;
      if (!stale) return;
      autoSyncedRef.current = true;
      // Fire-and-forget; handleSyncPayouts already manages UI state.
      handleSyncPayouts(false);
    })();
  }, [loadEbaySync]);

  const flash = (msg) => {
    setSavedFlash(msg);
    setTimeout(() => setSavedFlash(""), 1800);
  };

  const handleAdd = async (payload) => {
    setSubmitting(true);
    try {
      await ledgerAPI.createTransaction(payload);
      setShowAdd(false);
      flash("Added");
      await loadAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to add transaction");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (payload) => {
    if (!editing) return;
    setSubmitting(true);
    try {
      await ledgerAPI.updateTransaction(editing.id, payload);
      setEditing(null);
      flash("Saved");
      await loadAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to update transaction");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (txn) => {
    const ok = await confirm({
      title: "Delete transaction?",
      message: `${txn.date} — ${txn.description || "(no description)"} — ${fmtMoney(txn.amount)}`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await ledgerAPI.deleteTransaction(txn.id);
      await loadAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const handleAddCategory = async (name) => {
    try {
      await ledgerAPI.createCategory(name);
      await loadAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to add category");
    }
  };

  const handleDeleteCategory = async (c) => {
    const ok = await confirm({
      title: `Delete "${c.name}"?`,
      message: "Past transactions in this category will keep their label but the dropdown will no longer show it.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await ledgerAPI.deleteCategory(c.id);
      await loadAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to delete category");
    }
  };

  const toggleSort = (field) => {
    if (sort === field) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setSort(field);
      setOrder("desc");
    }
    setPage(1); // reset to first page when sort changes
  };

  const clearFilters = () => {
    setFilters({ from_date: "", to_date: "", category: "", type: "", q: "" });
  };

  // Compute running balance per row (relative to the displayed/sorted list,
  // but always anchored to chronological order so the running total is
  // meaningful regardless of how the user sorts).
  const runningBalanceMap = useMemo(() => {
    const chronological = [...transactions].sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
      return (a.created_at || "") < (b.created_at || "") ? -1 : 1;
    });
    const map = {};
    let running = 0;
    for (const t of chronological) {
      running += t.type === "deposit" ? t.amount : -t.amount;
      map[t.id] = Math.round(running * 100) / 100;
    }
    return map;
  }, [transactions]);

  const exportHref = ledgerAPI.exportCsvUrl(filters);

  const filtersActive = Object.values(filters).some((v) => v);

  // Reset to page 1 when filters or page size change so the user always sees results
  useEffect(() => {
    setPage(1);
  }, [filters, pageSize]);

  // Slice the (already sorted by backend) transactions into the current page
  const totalPages = Math.max(1, Math.ceil(transactions.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageTransactions = transactions.slice(pageStart, pageStart + pageSize);
  const pageRangeLabel =
    transactions.length === 0
      ? "0"
      : `${pageStart + 1}–${Math.min(pageStart + pageSize, transactions.length)}`;

  return (
    <div className="min-h-screen pb-28 bg-[#fafaf7]">
      {/* Top bar */}
      <div className="flex items-center justify-between p-4 border-b-2 border-black bg-white">
        <button
          onClick={() => navigate("/profile")}
          className="flex items-center gap-2 font-heading text-sm font-bold uppercase hover:opacity-60"
          data-testid="ledger-back-btn"
        >
          <ArrowLeft size={18} weight="bold" /> Back
        </button>
        <div className="font-heading text-lg font-bold uppercase tracking-tight">Ledger</div>
        {savedFlash ? (
          <div className="flex items-center gap-1 text-green-600">
            <Check size={16} weight="bold" />
            <span className="font-body text-xs font-bold">{savedFlash}</span>
          </div>
        ) : (
          <span className="w-12" />
        )}
      </div>

      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
        {/* Balance Card */}
        <div
          className={`border-4 border-black p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] ${
            balance.balance >= 0 ? "bg-white" : "bg-red-50"
          }`}
          data-testid="balance-card"
        >
          <div className="font-body text-[11px] font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-1">
            Running Balance
          </div>
          <div
            className={`font-heading text-4xl sm:text-5xl font-bold tracking-tight ${
              balance.balance >= 0 ? "text-black" : "text-red-700"
            }`}
            data-testid="balance-amount"
          >
            {fmtMoney(balance.balance)}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 font-body text-xs text-neutral-600">
            <span>
              <ArrowUp size={11} weight="bold" className="inline text-green-600 mr-0.5" />
              In: <strong>{fmtMoney(balance.total_deposits)}</strong> ({balance.deposit_count})
            </span>
            <span>
              <ArrowDown size={11} weight="bold" className="inline text-red-600 mr-0.5" />
              Out: <strong>{fmtMoney(balance.total_debits)}</strong> ({balance.debit_count})
            </span>
          </div>
        </div>

        {/* eBay Payout Sync — always visible once status is known, so users
            always know whether eBay is connected and what state it's in. */}
        {ebaySync.status !== "loading" && (
          <div className="border-2 border-black bg-white p-4" data-testid="ebay-sync-card">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-9 h-9 border-2 border-black bg-[#0064d2] text-white flex items-center justify-center shrink-0">
                  <Storefront size={18} weight="bold" />
                </div>
                <div className="min-w-0">
                  <div className="font-heading text-sm font-bold uppercase tracking-tight">eBay Payouts</div>
                  {ebaySync.status === "not_connected" ? (
                    <div className="font-body text-xs text-neutral-600 mt-0.5">
                      Connect eBay on the Profile page to auto-import payouts.
                    </div>
                  ) : ebaySync.status === "scope_missing" ? (
                    <div className="font-body text-xs text-amber-700 mt-0.5">
                      Reconnect eBay to enable payout sync (new permission added).
                    </div>
                  ) : ebaySync.status === "error" ? (
                    <div
                      className="font-body text-xs text-red-700 mt-0.5"
                      data-testid="ebay-sync-error"
                    >
                      eBay returned an error: {ebaySync.error || "unknown"}
                    </div>
                  ) : ebaySync.payouts.length === 0 ? (
                    <div className="font-body text-xs text-neutral-500 mt-0.5">
                      {ebaySync.last_sync_at
                        ? `Up to date · last checked ${new Date(ebaySync.last_sync_at).toLocaleString()}`
                        : `No payouts found in the last ${ebaySync.lookback_days || 730} days.`}
                    </div>
                  ) : (
                    <div className="font-body text-xs text-neutral-600 mt-0.5">
                      {(() => {
                        const total = ebaySync.payouts
                          .filter((p) => !ebaySync.already_imported_ids.includes(p.payout_id))
                          .reduce((s, p) => s + (p.amount || 0), 0);
                        const newCount = ebaySync.payouts.length - ebaySync.already_imported_ids.length;
                        return newCount > 0
                          ? `${newCount} new payout${newCount !== 1 ? "s" : ""} · ${fmtMoney(total)} ready to import`
                          : "Up to date · no new payouts";
                      })()}
                    </div>
                  )}
                  {syncMsg && (
                    <div className="font-body text-xs font-semibold text-green-700 mt-1" data-testid="ebay-sync-msg">
                      {syncMsg}
                    </div>
                  )}
                </div>
              </div>
              <div className="shrink-0">
                {ebaySync.status === "not_connected" ? (
                  <button
                    onClick={() => navigate("/profile")}
                    className="border-2 border-black bg-black text-white px-3 py-1.5 font-heading text-xs font-bold uppercase tracking-wide shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:bg-neutral-800"
                    data-testid="ebay-connect-btn"
                  >
                    Connect
                  </button>
                ) : ebaySync.status === "scope_missing" ? (
                  <button
                    onClick={() => navigate("/profile")}
                    className="border-2 border-black bg-amber-400 text-black px-3 py-1.5 font-heading text-xs font-bold uppercase tracking-wide hover:bg-amber-300"
                    data-testid="ebay-reconnect-btn"
                  >
                    Reconnect
                  </button>
                ) : (
                  <div className="flex flex-col items-end gap-1">
                    <button
                      onClick={() => handleSyncPayouts(false)}
                      disabled={syncing}
                      className="border-2 border-black bg-black text-white px-3 py-1.5 font-heading text-xs font-bold uppercase tracking-wide shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] disabled:opacity-50 hover:bg-neutral-800 flex items-center gap-1.5"
                      data-testid="ebay-sync-btn"
                    >
                      <ArrowsClockwise size={12} weight="bold" className={syncing ? "animate-spin" : ""} />
                      {syncing ? "Syncing…" : "Sync now"}
                    </button>
                    <button
                      onClick={() => handleSyncPayouts(true)}
                      disabled={syncing}
                      className="font-body text-[10px] text-neutral-500 underline hover:text-black disabled:opacity-50"
                      title="Re-scan the last 2 years and import any missing payouts"
                      data-testid="ebay-sync-full-btn"
                    >
                      Rescan 2yr
                    </button>
                    <button
                      onClick={handleImportById}
                      disabled={syncing}
                      className="font-body text-[10px] text-neutral-500 underline hover:text-black disabled:opacity-50"
                      title="If a payout is visible in eBay Seller Hub but missing from this list, paste its Payout ID to import it directly."
                      data-testid="ebay-import-by-id-btn"
                    >
                      Import by ID
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add Transaction */}
        <div className="border-4 border-black bg-white p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          {!showAdd ? (
            <button
              onClick={() => setShowAdd(true)}
              className="w-full bg-black text-white border-2 border-black px-4 py-3 font-heading text-sm font-bold uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:bg-neutral-800 flex items-center justify-center gap-2"
              data-testid="show-add-form"
            >
              <Plus size={18} weight="bold" /> Add Transaction
            </button>
          ) : (
            <>
              <SectionHeader
                title="New Transaction"
                right={
                  <button
                    onClick={() => setShowCatMgr(true)}
                    className="font-body text-xs font-semibold underline hover:opacity-60"
                    data-testid="manage-categories-link"
                  >
                    Manage categories
                  </button>
                }
              />
              <TransactionForm
                categories={categories}
                onSave={handleAdd}
                onCancel={() => setShowAdd(false)}
                submitting={submitting}
                onRequestNewCategory={() => setShowCatMgr(true)}
              />
            </>
          )}
        </div>

        {/* Filters — Desktop only. Mobile gets a single search bar below. */}
        <div className="hidden md:block border-2 border-black bg-white p-4">
          <div className="flex items-end justify-between mb-3">
            <div className="font-heading text-xs font-bold uppercase tracking-[0.1em]">Filters</div>
            {filtersActive && (
              <button
                onClick={clearFilters}
                className="font-body text-xs font-semibold underline hover:opacity-60"
                data-testid="clear-filters-btn"
              >
                Clear
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Field label="From">
              <input
                type="date"
                value={filters.from_date}
                onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                className="w-full border-2 border-black bg-white px-2 py-1.5 font-body text-xs focus:outline-none focus:ring-4 focus:ring-black/20"
                data-testid="filter-from"
              />
            </Field>
            <Field label="To">
              <input
                type="date"
                value={filters.to_date}
                onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                className="w-full border-2 border-black bg-white px-2 py-1.5 font-body text-xs focus:outline-none focus:ring-4 focus:ring-black/20"
                data-testid="filter-to"
              />
            </Field>
            <Field label="Category">
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="w-full border-2 border-black bg-white px-2 py-1.5 font-body text-xs focus:outline-none focus:ring-4 focus:ring-black/20"
                data-testid="filter-category"
              >
                <option value="">All</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Type">
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className="w-full border-2 border-black bg-white px-2 py-1.5 font-body text-xs focus:outline-none focus:ring-4 focus:ring-black/20"
                data-testid="filter-type"
              >
                <option value="">All</option>
                <option value="deposit">Deposit</option>
                <option value="debit">Debit</option>
              </select>
            </Field>
            <Field label="Search">
              <input
                type="text"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Description..."
                className="w-full border-2 border-black bg-white px-2 py-1.5 font-body text-xs focus:outline-none focus:ring-4 focus:ring-black/20"
                data-testid="filter-search"
              />
            </Field>
          </div>
        </div>

        {/* Mobile-only search bar */}
        <div className="md:hidden">
          <input
            type="text"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Search transactions..."
            className="w-full border-2 border-black bg-white px-3 py-2 font-body text-sm focus:outline-none focus:ring-4 focus:ring-black/20"
            data-testid="mobile-search"
          />
        </div>

        {/* Transaction Table */}
        <div className="border-4 border-black bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] overflow-hidden">
          <div className="flex items-center justify-between p-3 border-b-2 border-black bg-neutral-50">
            <div className="font-heading text-sm font-bold uppercase tracking-[0.1em]">
              Transactions ({transactions.length})
            </div>
            <a
              href={exportHref}
              className="hidden md:flex items-center gap-1 font-heading text-xs font-bold uppercase border-2 border-black bg-white px-2 py-1 hover:bg-neutral-100"
              data-testid="csv-export-btn"
            >
              <Download size={14} weight="bold" /> CSV
            </a>
          </div>

          {loading ? (
            <div className="p-6 text-center font-body text-sm text-neutral-500">Loading...</div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center font-body text-sm text-neutral-500" data-testid="txn-empty-state">
              No transactions yet. Add your first one above.
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full" data-testid="txn-table">
                  <thead>
                    <tr>
                      <SortHeader field="date" label="Date" sort={sort} order={order} onSort={toggleSort} />
                      <SortHeader field="description" label="Description" sort={sort} order={order} onSort={toggleSort} />
                      <SortHeader field="category" label="Category" sort={sort} order={order} onSort={toggleSort} />
                      <SortHeader field="type" label="Type" sort={sort} order={order} onSort={toggleSort} />
                      <SortHeader field="amount" label="Amount" sort={sort} order={order} onSort={toggleSort} align="right" />
                      <th className="px-3 py-2 font-heading text-[11px] font-bold uppercase tracking-wide text-right border-b-2 border-black bg-neutral-100">
                        Balance
                      </th>
                      <th className="px-3 py-2 border-b-2 border-black bg-neutral-100 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageTransactions.map((t) => (
                      <tr
                        key={t.id}
                        className="border-b border-neutral-200 hover:bg-neutral-50"
                        data-testid={`txn-row-${t.id}`}
                      >
                        <td className="px-3 py-2 font-body text-sm whitespace-nowrap">{t.date}</td>
                        <td className="px-3 py-2 font-body text-sm">
                          {t.external_source === "ebay_payout" && (
                            <Storefront
                              size={12}
                              weight="bold"
                              className="inline-block mr-1.5 text-[#0064d2] align-[-1px]"
                              title="Imported from eBay"
                            />
                          )}
                          {t.description || <span className="text-neutral-400">—</span>}
                          {isPendingPayout(t) && <PendingBadge />}
                        </td>
                        <td className="px-3 py-2 font-body text-xs">
                          {t.category ? (
                            <span className="inline-block border border-black px-2 py-0.5 bg-neutral-50">{t.category}</span>
                          ) : (
                            <span className="text-neutral-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-body text-xs">
                          {t.type === "deposit" ? (
                            <span className="text-green-700 font-bold">DEPOSIT</span>
                          ) : (
                            <span className="text-red-700 font-bold">DEBIT</span>
                          )}
                        </td>
                        <td className={`px-3 py-2 font-mono text-sm text-right whitespace-nowrap ${t.type === "deposit" ? "text-green-700" : "text-red-700"}`}>
                          {t.type === "deposit" ? "+" : "−"}
                          {fmtMoney(t.amount).replace("-", "")}
                        </td>
                        <td className="px-3 py-2 font-mono text-sm text-right whitespace-nowrap">
                          {fmtMoney(runningBalanceMap[t.id])}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button
                            onClick={() => setEditing(t)}
                            className="text-neutral-600 hover:text-black mr-2"
                            data-testid={`txn-edit-${t.id}`}
                            aria-label="Edit"
                          >
                            <PencilSimple size={16} weight="bold" />
                          </button>
                          <button
                            onClick={() => handleDelete(t)}
                            className="text-red-600 hover:text-red-800"
                            data-testid={`txn-delete-${t.id}`}
                            aria-label="Delete"
                          >
                            <Trash size={16} weight="bold" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile list */}
              <ul className="md:hidden divide-y divide-neutral-200">
                {pageTransactions.map((t) => (
                  <li key={t.id} className="p-3 flex items-start justify-between gap-2" data-testid={`txn-card-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-body text-xs text-neutral-500">{t.date}</span>
                        {t.category && (
                          <span className="inline-block border border-black px-1.5 text-[10px] bg-neutral-50">
                            {t.category}
                          </span>
                        )}
                      </div>
                      <div className="font-body text-sm font-semibold truncate">
                        {t.external_source === "ebay_payout" && (
                          <Storefront
                            size={11}
                            weight="bold"
                            className="inline-block mr-1 text-[#0064d2] align-[-1px]"
                          />
                        )}
                        {t.description || <span className="text-neutral-400">(no description)</span>}
                        {isPendingPayout(t) && <PendingBadge />}
                      </div>
                      <div className="font-body text-xs text-neutral-500 mt-0.5">
                        Bal: <strong>{fmtMoney(runningBalanceMap[t.id])}</strong>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-mono text-sm font-bold ${t.type === "deposit" ? "text-green-700" : "text-red-700"}`}>
                        {t.type === "deposit" ? "+" : "−"}
                        {fmtMoney(t.amount).replace("-", "")}
                      </div>
                      <div className="flex gap-2 mt-1 justify-end">
                        <button onClick={() => setEditing(t)} className="text-neutral-500" data-testid={`txn-edit-${t.id}`}>
                          <PencilSimple size={16} weight="bold" />
                        </button>
                        <button onClick={() => handleDelete(t)} className="text-red-600" data-testid={`txn-delete-${t.id}`}>
                          <Trash size={16} weight="bold" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Pagination footer */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-3 py-2 border-t-2 border-black bg-neutral-50">
                <div className="flex items-center gap-2 font-body text-xs text-neutral-600">
                  <span>Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="border-2 border-black bg-white px-2 py-1 font-body text-xs focus:outline-none focus:ring-4 focus:ring-black/20"
                    data-testid="page-size-select"
                  >
                    <option value={5}>5</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="hidden sm:inline">
                    · {pageRangeLabel} of {transactions.length}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage <= 1}
                    className="border-2 border-black bg-white px-2 py-1 font-heading text-[11px] font-bold uppercase tracking-wide disabled:opacity-30 hover:bg-neutral-100"
                    data-testid="page-prev"
                  >
                    Prev
                  </button>
                  <span className="font-body text-xs text-neutral-600" data-testid="page-indicator">
                    Page {safePage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage >= totalPages}
                    className="border-2 border-black bg-white px-2 py-1 font-heading text-[11px] font-bold uppercase tracking-wide disabled:opacity-30 hover:bg-neutral-100"
                    data-testid="page-next"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Monthly Summary — Desktop only */}
        <div className="hidden md:block border-2 border-black bg-white">
          <button
            onClick={() => setShowSummary((s) => !s)}
            className="w-full flex items-center justify-between p-3 border-b-2 border-black bg-neutral-50 hover:bg-neutral-100"
            data-testid="summary-toggle"
          >
            <span className="font-heading text-sm font-bold uppercase tracking-[0.1em]">Summary</span>
            {showSummary ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
          </button>
          {showSummary && (
            <div className="p-4 space-y-4" data-testid="summary-panel">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="border-2 border-black p-2">
                  <div className="font-body text-[10px] uppercase tracking-wide text-neutral-500">In</div>
                  <div className="font-heading text-base font-bold text-green-700">{fmtMoney(summary.total_deposits)}</div>
                </div>
                <div className="border-2 border-black p-2">
                  <div className="font-body text-[10px] uppercase tracking-wide text-neutral-500">Out</div>
                  <div className="font-heading text-base font-bold text-red-700">{fmtMoney(summary.total_debits)}</div>
                </div>
                <div className="border-2 border-black p-2">
                  <div className="font-body text-[10px] uppercase tracking-wide text-neutral-500">Net</div>
                  <div className={`font-heading text-base font-bold ${summary.net >= 0 ? "text-black" : "text-red-700"}`}>
                    {fmtMoney(summary.net)}
                  </div>
                </div>
              </div>

              <div>
                <div className="font-heading text-xs font-bold uppercase tracking-[0.1em] mb-2">By Month</div>
                {summary.monthly.length === 0 ? (
                  <div className="font-body text-xs text-neutral-500 italic">No data.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-black">
                        <th className="text-left font-heading text-[10px] uppercase py-1">Month</th>
                        <th className="text-right font-heading text-[10px] uppercase py-1">In</th>
                        <th className="text-right font-heading text-[10px] uppercase py-1">Out</th>
                        <th className="text-right font-heading text-[10px] uppercase py-1">Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.monthly.map((m) => (
                        <tr key={m.month} className="border-b border-neutral-200">
                          <td className="font-body text-xs py-1">{m.month}</td>
                          <td className="font-mono text-xs py-1 text-right text-green-700">{fmtMoney(m.deposits)}</td>
                          <td className="font-mono text-xs py-1 text-right text-red-700">{fmtMoney(m.debits)}</td>
                          <td className={`font-mono text-xs py-1 text-right ${m.net >= 0 ? "" : "text-red-700"}`}>
                            {fmtMoney(m.net)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div>
                <div className="font-heading text-xs font-bold uppercase tracking-[0.1em] mb-2">By Category</div>
                {summary.by_category.length === 0 ? (
                  <div className="font-body text-xs text-neutral-500 italic">No data.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-black">
                        <th className="text-left font-heading text-[10px] uppercase py-1">Category</th>
                        <th className="text-right font-heading text-[10px] uppercase py-1">In</th>
                        <th className="text-right font-heading text-[10px] uppercase py-1">Out</th>
                        <th className="text-right font-heading text-[10px] uppercase py-1">Net</th>
                        <th className="text-right font-heading text-[10px] uppercase py-1">#</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.by_category.map((c) => (
                        <tr key={c.category} className="border-b border-neutral-200">
                          <td className="font-body text-xs py-1">{c.category}</td>
                          <td className="font-mono text-xs py-1 text-right text-green-700">{fmtMoney(c.deposits)}</td>
                          <td className="font-mono text-xs py-1 text-right text-red-700">{fmtMoney(c.debits)}</td>
                          <td className={`font-mono text-xs py-1 text-right ${c.net >= 0 ? "" : "text-red-700"}`}>
                            {fmtMoney(c.net)}
                          </td>
                          <td className="font-mono text-xs py-1 text-right text-neutral-500">{c.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editing && (
        <div
          className="fixed inset-0 bg-black/60 z-[10001] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setEditing(null)}
          data-testid="edit-modal"
        >
          <div
            className="w-full sm:max-w-md bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b-2 border-black p-4">
              <div className="font-heading text-lg font-bold uppercase tracking-tight">Edit Transaction</div>
              <button onClick={() => setEditing(null)} className="hover:opacity-60" data-testid="edit-close">
                <X size={22} weight="bold" />
              </button>
            </div>
            <div className="p-4">
              <TransactionForm
                initial={editing}
                categories={categories}
                onSave={handleEdit}
                onCancel={() => setEditing(null)}
                submitting={submitting}
                onRequestNewCategory={() => setShowCatMgr(true)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Category Manager Modal */}
      {showCatMgr && (
        <CategoryManager
          categories={categories}
          onAdd={handleAddCategory}
          onDelete={handleDeleteCategory}
          onClose={() => setShowCatMgr(false)}
        />
      )}

      <BottomNav />
    </div>
  );
}
